// The in-match HUD, lobby and results overlays. Builds its own DOM under a root element and
// injects one style element. update() runs every frame, so every write goes through a cache
// and only touches the DOM when the value changed. Lists rebuild only when their signature changes.

import { KIND_NAMES, KIND_COLORS, KILL_FALL, KILL_STAB, KILL_KNIFE } from "@fragout/shared";

export type HudAction = "ready" | "addBot" | "removeBot" | "vote" | "rematch" | "back";

export interface HudPlayerView {
  id: string; name: string; score: number; kills: number; deaths: number;
  me: boolean; connected: boolean; bot: boolean; ready: boolean; vote: boolean;
}
export interface HudSlotView { kind: number; ammo: number; label: string }
export interface HudPadView { id: string; label: string; kind: number; active: boolean; nextInS: number; clock: boolean }

export interface HudView {
  phase: number;            // 0 lobby, 1 countdown, 2 playing, 3 overtime, 4 results
  phaseLeftS: number;
  matchS: number;           // seconds since match start
  myName: string;
  alive: boolean;
  respawnLeftS: number;
  killerName: string;
  shield: number;           // 0 to 100
  flashing: boolean;
  cookProgress: number;     // -1 not cooking, else 0 to 1 of the fuse used
  cookKind: number;
  slot: number;             // 1 to 4
  slots: HudSlotView[];     // length 4, slot 1 Frag with ammo -1, slot 4 knife with kind 0
  fragCooldown: number;     // 0 to 1, 0 ready
  knifeOut: boolean;
  knifeReturnS: number;
  players: HudPlayerView[];
  pads: HudPadView[];
  scoreLimit: number;
  leader: string;
  leaderScore: number;
  myScore: number;
  winner: string;
  rtt: number;
  fps: number;
  showScoreboard: boolean;
  pointerLocked: boolean;
  /** Why the browser refused the mouse, if it did. Shown instead of the click prompt. */
  lockHint: string;
  humans: number;
  bots: number;
  countdownS: number;
  hint: string;
}

const PAPER = "#F2F2EE", INK = "#14161F", ORANGE = "#FF7A1A", MUTED = "#5A6070", GREEN = "#5EE22E", RED = "#FF3B3B";
const RING_R = 15;
const RING_C = 2 * Math.PI * RING_R;
const FEED_LIFE_MS = 6000;
const FEED_MAX = 6;
const SVG_NS = "http://www.w3.org/2000/svg";

const CSS = `
.fo{position:fixed;inset:0;pointer-events:none;z-index:10;color:${INK};font-family:Bahnschrift,"Segoe UI",Arial,sans-serif;font-stretch:condensed;font-size:14px;line-height:1.2;text-transform:uppercase;letter-spacing:.5px;user-select:none}
.fo *{box-sizing:border-box}
.fo[hidden],.fo [hidden]{display:none!important}
.fo .panel{background:rgba(242,242,238,.94);border:3px solid ${INK};border-radius:14px;box-shadow:4px 4px 0 ${INK}}
.fo .pill{display:inline-block;padding:4px 12px;border-radius:999px;background:${INK};color:${PAPER};font-weight:700}
.fo .muted{color:${MUTED}}
.fo .hot{color:${ORANGE}}
.fo button{pointer-events:auto;cursor:pointer;font:inherit;font-weight:700;font-size:18px;letter-spacing:1px;text-transform:uppercase;padding:10px 22px;border:3px solid ${INK};border-radius:999px;background:${PAPER};color:${INK};box-shadow:4px 4px 0 ${INK}}
.fo button:active{transform:translate(2px,2px);box-shadow:2px 2px 0 ${INK}}
.fo button.big{font-size:26px;padding:14px 44px;background:${GREEN}}
.fo button.big.on{background:${ORANGE}}
.fo button.small{font-size:14px;padding:6px 14px;box-shadow:3px 3px 0 ${INK}}
.fo button.small:active{box-shadow:1px 1px 0 ${INK}}

.fo-cross{position:absolute;left:50%;top:50%;width:44px;height:44px;margin:-22px 0 0 -22px}
.fo-cross .dot{position:absolute;left:50%;top:50%;width:6px;height:6px;margin:-3px 0 0 -3px;border-radius:50%;background:#fff;box-shadow:0 0 0 2px ${INK};transition:transform .08s}
.fo-cross.hit .dot{transform:scale(1.9);background:${ORANGE}}
.fo-cross .tick{position:absolute;left:50%;top:50%;width:4px;height:9px;margin:-4px 0 0 -2px;background:${ORANGE};border:1px solid ${INK};border-radius:2px;opacity:0;transform-origin:50% 50%}
.fo-cross.hit .tick{opacity:1}
.fo-cross svg{position:absolute;inset:0;transform:rotate(-90deg);opacity:0}
.fo-cross svg.on{opacity:1}
.fo-cross svg .track{fill:none;stroke:rgba(20,22,31,.35);stroke-width:5}
.fo-cross svg .fill{fill:none;stroke-width:5;stroke-linecap:butt}
.fo-cross.pulse svg{animation:fo-pulse .3s ease-out}
@keyframes fo-pulse{0%{transform:rotate(-90deg) scale(1)}50%{transform:rotate(-90deg) scale(1.5)}100%{transform:rotate(-90deg) scale(1)}}

.fo-top{position:absolute;left:50%;top:14px;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:8px}
.fo-score{padding:6px 18px 8px;text-align:center;min-width:220px}
.fo-score .mode{font-size:12px;font-weight:700;color:${MUTED};letter-spacing:2px}
.fo-score .mode.hot{color:${ORANGE}}
.fo-score .time{font-size:30px;font-weight:700;line-height:1;margin:2px 0 4px;font-variant-numeric:tabular-nums}
.fo-score .time.hot{color:${ORANGE}}
.fo-score .row{display:flex;justify-content:center;gap:16px;font-weight:700}
.fo-score .row b{color:${ORANGE}}
.fo-shield{display:flex;align-items:center;gap:8px;padding:4px 10px;border-radius:999px;font-weight:700;font-size:12px}
.fo-shield .track{width:100px;height:12px;border-radius:999px;background:rgba(20,22,31,.25);overflow:hidden;border:2px solid ${INK}}
.fo-shield .bar{height:100%;width:100%;background:${GREEN};border-radius:999px}
.fo-shield .num{min-width:26px;text-align:right;font-variant-numeric:tabular-nums}
.fo-shield.flash{animation:fo-flash .18s steps(2) infinite}
.fo-shield.flash .bar{background:${RED}}
@keyframes fo-flash{0%{background:rgba(242,242,238,.94)}100%{background:${RED}}}

.fo-feed{position:absolute;left:14px;top:14px;display:flex;flex-direction:column;gap:6px;max-width:44vw}
.fo-feed .entry{display:inline-flex;align-items:center;gap:8px;padding:4px 12px;border-radius:999px;background:rgba(242,242,238,.94);border:2px solid ${INK};font-weight:700;white-space:nowrap;align-self:flex-start;animation:fo-fade ${FEED_LIFE_MS}ms linear forwards}
.fo-feed .entry.me{background:${ORANGE};color:${INK}}
.fo-feed .kind{padding:1px 8px;border-radius:999px;border:2px solid ${INK};font-size:12px}
@keyframes fo-fade{0%,80%{opacity:1}100%{opacity:0}}

.fo-slots{position:absolute;left:14px;bottom:14px;display:flex;gap:8px;align-items:flex-end}
.fo-slot{width:96px;padding:6px 8px 8px;border-radius:12px;background:rgba(242,242,238,.94);border:3px solid ${INK};position:relative}
.fo-slot.sel{box-shadow:0 0 0 3px var(--kc),4px 4px 0 ${INK}}
.fo-slot.dim{opacity:.45}
.fo-slot .n{position:absolute;right:6px;top:4px;font-size:11px;font-weight:700;color:${MUTED}}
.fo-slot .band{height:8px;border-radius:999px;background:var(--kc);border:2px solid ${INK};margin:14px 0 6px}
.fo-slot .name{font-weight:700;font-size:13px;white-space:nowrap;overflow:hidden}
.fo-slot .ammo{font-size:20px;font-weight:700;line-height:1;font-variant-numeric:tabular-nums}
.fo-slot .cd{position:absolute;left:8px;right:8px;bottom:-9px;height:5px;border-radius:999px;background:${INK};overflow:hidden}
.fo-slot .cd i{display:block;height:100%;background:${ORANGE};width:0}

.fo-pads{position:absolute;right:14px;top:14px;display:flex;flex-direction:column;gap:4px;padding:8px 10px;border-radius:12px;min-width:150px}
.fo-pads .row{display:flex;align-items:center;gap:8px;font-weight:700;font-size:13px}
.fo-pads .row.dim{opacity:.45}
.fo-pads .dot{width:12px;height:12px;border-radius:50%;border:2px solid ${INK};flex:none}
.fo-pads .lbl{flex:1}
.fo-pads .st{font-variant-numeric:tabular-nums;min-width:40px;text-align:right}
.fo-pads .st.ready{color:${GREEN};text-shadow:0 0 0 ${INK},1px 1px 0 ${INK}}

.fo-overlay{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(20,22,31,.45)}
.fo-overlay .card{padding:22px 28px;min-width:360px;max-width:640px;text-align:center}
.fo-overlay h1{margin:0 0 4px;font-size:40px;letter-spacing:3px;line-height:1}
.fo-overlay h1 span{color:${ORANGE}}
.fo-overlay h2{margin:0 0 12px;font-size:16px;color:${MUTED};letter-spacing:2px}
.fo-overlay .big-num{font-size:56px;font-weight:700;line-height:1;color:${ORANGE};font-variant-numeric:tabular-nums}
.fo-overlay .actions{display:flex;gap:12px;justify-content:center;align-items:center;margin-top:16px;flex-wrap:wrap}
.fo-overlay .hint{margin-top:14px;font-size:12px;color:${MUTED};text-transform:none;letter-spacing:0;word-break:break-all}
.fo-table{width:100%;border-collapse:collapse;margin:8px 0;font-size:15px}
.fo-table th{font-size:11px;color:${MUTED};letter-spacing:1.5px;text-align:right;padding:2px 8px}
.fo-table th:first-child,.fo-table td:first-child{text-align:left}
.fo-table td{padding:5px 8px;text-align:right;font-weight:700;font-variant-numeric:tabular-nums;border-top:2px solid rgba(20,22,31,.12)}
.fo-table tr.me td{background:${ORANGE};color:${INK}}
.fo-table tr.away td{opacity:.5}
.fo-table td .tag{font-size:10px;padding:1px 6px;border-radius:999px;background:${INK};color:${PAPER};margin-left:6px;vertical-align:middle}
.fo-table td .tick{color:${GREEN};text-shadow:1px 1px 0 ${INK};margin-left:6px}
.fo-death .card{background:rgba(242,242,238,.94)}
.fo-death .who{font-size:28px;font-weight:700;margin:4px 0 10px}
.fo-death .who span{color:${RED}}

.fo-lock{position:absolute;left:50%;bottom:22px;transform:translateX(-50%);font-size:14px;letter-spacing:1px}
.fo-net{position:absolute;right:14px;bottom:14px;font-size:11px;font-weight:700;color:${MUTED};font-variant-numeric:tabular-nums;background:rgba(242,242,238,.8);padding:2px 8px;border-radius:999px}
.fo-hurt{position:absolute;inset:0;box-shadow:inset 0 0 0 0 rgba(255,59,59,0);transition:box-shadow .25s}
.fo-hurt.on{box-shadow:inset 0 0 120px 30px rgba(255,59,59,.55);transition:none}
`;

let styleInjected = false;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, parent?: Element, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  parent?.appendChild(e);
  return e;
}

function mmss(s: number): string {
  const t = Math.max(0, Math.floor(s));
  const m = Math.floor(t / 60), r = t % 60;
  return `${m}:${r < 10 ? "0" : ""}${r}`;
}

function ordinal(n: number): string {
  const r = n % 100;
  if (r >= 11 && r <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

function kindColor(kind: number): string {
  return KIND_COLORS[kind] ?? "#ffffff";
}

function kindName(kind: number): string {
  if (kind === KILL_STAB) return "STAB";
  if (kind === KILL_KNIFE) return "KNIFE";
  if (kind === KILL_FALL) return "FELL";
  return KIND_NAMES[kind] ?? "?";
}

function sortPlayers(players: HudPlayerView[]): HudPlayerView[] {
  return players.slice().sort((a, b) => b.score - a.score || b.kills - a.kills || a.deaths - b.deaths || a.name.localeCompare(b.name));
}

export class Hud {
  private readonly box: HTMLElement;
  private readonly onAction: (action: HudAction) => void;

  // caches so update() only writes what changed
  private readonly texts = new Map<Element, string>();
  private readonly styles = new Map<Element, string>();
  private readonly sigs = new Map<string, string>();

  private readonly cross: HTMLElement;
  private readonly ring: SVGSVGElement;
  private readonly ringFill: SVGCircleElement;
  private readonly hurtEl: HTMLElement;
  private readonly feed: HTMLElement;
  private readonly top: HTMLElement;
  private readonly modeEl: HTMLElement;
  private readonly timeEl: HTMLElement;
  private readonly youEl: HTMLElement;
  private readonly leadEl: HTMLElement;
  private readonly shieldEl: HTMLElement;
  private readonly shieldBar: HTMLElement;
  private readonly shieldNum: HTMLElement;
  private readonly slotsEl: HTMLElement;
  private readonly slotEls: { root: HTMLElement; name: HTMLElement; ammo: HTMLElement }[] = [];
  private readonly cdBar: HTMLElement;
  private readonly padsEl: HTMLElement;
  private readonly boardEl: HTMLElement;
  private readonly boardTable: HTMLElement;
  private readonly deathEl: HTMLElement;
  private readonly deathTitle: HTMLElement;
  private readonly deathWho: HTMLElement;
  private readonly deathIn: HTMLElement;
  private readonly lobbyEl: HTMLElement;
  private readonly lobbySub: HTMLElement;
  private readonly lobbyCount: HTMLElement;
  private readonly lobbyList: HTMLElement;
  private readonly readyBtn: HTMLButtonElement;
  private readonly lobbyHint: HTMLElement;
  private readonly resultsEl: HTMLElement;
  private readonly winnerEl: HTMLElement;
  private readonly resultsTable: HTMLElement;
  private readonly voteBtn: HTMLButtonElement;
  private readonly resultsLeft: HTMLElement;
  private readonly lockEl: HTMLElement;
  private readonly netEl: HTMLElement;

  private lastCook = -1;
  private hitTimer = 0;
  private hurtTimer = 0;
  private pulseTimer = 0;

  constructor(root: HTMLElement, onAction: (action: HudAction) => void) {
    this.onAction = onAction;
    if (!styleInjected) {
      const style = document.createElement("style");
      style.textContent = CSS;
      document.head.appendChild(style);
      styleInjected = true;
    }
    const box = el("div", "fo", root);
    this.box = box;

    this.hurtEl = el("div", "fo-hurt", box);

    // crosshair and cook ring
    this.cross = el("div", "fo-cross", box);
    this.ring = document.createElementNS(SVG_NS, "svg");
    this.ring.setAttribute("viewBox", "0 0 44 44");
    const track = document.createElementNS(SVG_NS, "circle");
    track.setAttribute("class", "track");
    track.setAttribute("cx", "22"); track.setAttribute("cy", "22"); track.setAttribute("r", String(RING_R));
    this.ringFill = document.createElementNS(SVG_NS, "circle");
    this.ringFill.setAttribute("class", "fill");
    this.ringFill.setAttribute("cx", "22"); this.ringFill.setAttribute("cy", "22"); this.ringFill.setAttribute("r", String(RING_R));
    this.ringFill.setAttribute("stroke-dasharray", String(RING_C));
    this.ringFill.setAttribute("stroke-dashoffset", String(RING_C));
    this.ring.appendChild(track);
    this.ring.appendChild(this.ringFill);
    this.cross.appendChild(this.ring);
    for (let i = 0; i < 4; i++) {
      const t = el("div", "tick", this.cross);
      t.style.transform = `rotate(${i * 90 + 45}deg) translateY(-14px)`;
    }
    el("div", "dot", this.cross);

    // score box and shield
    this.top = el("div", "fo-top", box);
    const score = el("div", "panel fo-score", this.top);
    this.modeEl = el("div", "mode", score, "FREE FOR ALL");
    this.timeEl = el("div", "time", score, "0:00");
    const row = el("div", "row", score);
    this.youEl = el("span", "", row);
    this.leadEl = el("span", "", row);
    this.shieldEl = el("div", "panel fo-shield", this.top);
    el("span", "", this.shieldEl, "SHIELD");
    const track2 = el("div", "track", this.shieldEl);
    this.shieldBar = el("div", "bar", track2);
    this.shieldNum = el("span", "num", this.shieldEl, "100");

    this.feed = el("div", "fo-feed", box);

    // weapon slots
    this.slotsEl = el("div", "fo-slots", box);
    for (let i = 0; i < 4; i++) {
      const s = el("div", "fo-slot", this.slotsEl);
      el("span", "n", s, String(i + 1));
      el("div", "band", s);
      const name = el("div", "name", s);
      const ammo = el("div", "ammo", s);
      this.slotEls.push({ root: s, name, ammo });
    }
    const cd = el("div", "cd", this.slotEls[0].root);
    this.cdBar = el("i", "", cd);

    this.padsEl = el("div", "panel fo-pads", box);

    // scoreboard
    this.boardEl = el("div", "fo-overlay", box);
    const boardCard = el("div", "panel card", this.boardEl);
    el("h2", "", boardCard, "SCOREBOARD");
    this.boardTable = el("div", "", boardCard);

    // death overlay
    this.deathEl = el("div", "fo-overlay fo-death", box);
    this.deathEl.style.background = "transparent";
    const deathCard = el("div", "panel card", this.deathEl);
    this.deathTitle = el("h2", "", deathCard, "KILLED BY");
    this.deathWho = el("div", "who", deathCard);
    el("h2", "", deathCard, "RESPAWN IN");
    this.deathIn = el("div", "big-num", deathCard);

    // lobby
    this.lobbyEl = el("div", "fo-overlay", box);
    const lobbyCard = el("div", "panel card", this.lobbyEl);
    const h1 = el("h1", "", lobbyCard, "FRAG ");
    el("span", "", h1, "OUT");
    this.lobbySub = el("h2", "", lobbyCard, "FREE FOR ALL");
    this.lobbyCount = el("div", "big-num", lobbyCard);
    this.lobbyList = el("div", "", lobbyCard);
    const actions = el("div", "actions", lobbyCard);
    this.readyBtn = el("button", "big", actions, "READY");
    this.readyBtn.addEventListener("click", () => this.onAction("ready"));
    const botRow = el("div", "actions", lobbyCard);
    botRow.style.marginTop = "8px";
    el("button", "small", botRow, "ADD BOT").addEventListener("click", () => this.onAction("addBot"));
    el("button", "small", botRow, "REMOVE BOT").addEventListener("click", () => this.onAction("removeBot"));
    this.lobbyHint = el("div", "hint", lobbyCard);

    // results
    this.resultsEl = el("div", "fo-overlay", box);
    const resultsCard = el("div", "panel card", this.resultsEl);
    el("h2", "", resultsCard, "WINNER");
    this.winnerEl = el("div", "who", resultsCard);
    this.winnerEl.style.cssText = "font-size:34px;font-weight:700;color:" + ORANGE + ";margin-bottom:8px";
    this.resultsTable = el("div", "", resultsCard);
    const resActions = el("div", "actions", resultsCard);
    this.voteBtn = el("button", "big", resActions, "REMATCH");
    this.voteBtn.addEventListener("click", () => this.onAction("vote"));
    el("button", "small", resActions, "LOBBY").addEventListener("click", () => this.onAction("back"));
    this.resultsLeft = el("div", "hint", resultsCard);

    this.lockEl = el("div", "pill fo-lock", box, "CLICK TO PLAY. WASD MOVE, SPACE JUMP, HOLD CLICK TO COOK, TAB SCORES");
    this.netEl = el("div", "fo-net", box);

    for (const e of [this.boardEl, this.deathEl, this.lobbyEl, this.resultsEl, this.lockEl]) e.hidden = true;
  }

  // ---- cached writers

  private text(e: Element, s: string): void {
    if (this.texts.get(e) === s) return;
    this.texts.set(e, s);
    e.textContent = s;
  }

  private style(e: HTMLElement, prop: string, value: string): void {
    const key = prop + "\0" + value;
    if (this.styles.get(e) === key) return;
    this.styles.set(e, key);
    e.style.setProperty(prop, value);
  }

  private cls(e: Element, name: string, on: boolean): void {
    if (e.classList.contains(name) !== on) e.classList.toggle(name, on);
  }

  private show(e: HTMLElement, on: boolean): void {
    if (e.hidden === on) e.hidden = !on;
  }

  private changed(key: string, sig: string): boolean {
    if (this.sigs.get(key) === sig) return false;
    this.sigs.set(key, sig);
    return true;
  }

  // ---- public

  setVisible(v: boolean): void {
    this.show(this.box, v);
  }

  update(view: HudView): void {
    const inMatch = view.phase === 2 || view.phase === 3;
    const lobby = view.phase === 0 || view.phase === 1;
    const results = view.phase === 4;

    this.show(this.cross, inMatch && view.alive);
    this.show(this.top, inMatch);
    this.show(this.slotsEl, inMatch);
    this.show(this.padsEl, inMatch && view.pads.length > 0);
    this.show(this.feed, inMatch);
    this.show(this.boardEl, inMatch && view.showScoreboard);
    this.show(this.deathEl, inMatch && !view.alive && !view.showScoreboard);
    this.show(this.lobbyEl, lobby);
    this.show(this.resultsEl, results);
    // Prompt from the countdown, not just once the match is live, so the mouse is already yours at the start.
    this.show(this.lockEl, (inMatch || view.phase === 1) && !view.pointerLocked);
    this.text(this.lockEl, view.lockHint
      ? `THE BROWSER REFUSED THE MOUSE: ${view.lockHint}`
      : "CLICK TO PLAY. WASD MOVE, SPACE JUMP, HOLD CLICK TO COOK, TAB SCORES");

    if (inMatch) {
      this.updateCross(view);
      this.updateTop(view);
      this.updateSlots(view);
      this.updatePads(view);
      if (view.showScoreboard) this.updateTable(this.boardTable, "board", view, false);
      if (!view.alive) {
        // No killer yet means we joined a match in progress and are waiting for the first spawn.
        this.text(this.deathTitle, view.killerName ? "KILLED BY" : "JOINING");
        this.text(this.deathWho, view.killerName || "");
        this.text(this.deathIn, String(Math.max(0, Math.ceil(view.respawnLeftS))));
      }
    } else {
      this.lastCook = -1;
    }
    if (lobby) this.updateLobby(view);
    if (results) this.updateResults(view);

    this.text(this.netEl, `${Math.round(view.rtt)} MS  ${Math.round(view.fps)} FPS`);
  }

  killFeed(killerName: string, victimName: string, kind: number, me: boolean): void {
    const entry = el("div", "entry" + (me ? " me" : ""));
    el("span", "", entry, killerName);
    const k = el("span", "kind", entry, kindName(kind));
    k.style.background = kind === 0 ? PAPER : kindColor(kind);
    k.style.color = INK;
    el("span", "", entry, victimName);
    this.feed.appendChild(entry);
    while (this.feed.childElementCount > FEED_MAX) this.feed.firstElementChild?.remove();
    window.setTimeout(() => entry.remove(), FEED_LIFE_MS + 100);
  }

  hitMarker(): void {
    this.cross.classList.add("hit");
    window.clearTimeout(this.hitTimer);
    this.hitTimer = window.setTimeout(() => this.cross.classList.remove("hit"), 140);
  }

  hurt(): void {
    this.hurtEl.classList.add("on");
    window.clearTimeout(this.hurtTimer);
    this.hurtTimer = window.setTimeout(() => this.hurtEl.classList.remove("on"), 120);
  }

  // ---- sections

  private updateCross(view: HudView): void {
    const cooking = view.cookProgress >= 0;
    this.cls(this.ring, "on", cooking);
    if (cooking) {
      const p = Math.min(1, Math.max(0, view.cookProgress));
      const offset = (RING_C * (1 - p)).toFixed(2);
      if (this.ringFill.getAttribute("stroke-dashoffset") !== offset) this.ringFill.setAttribute("stroke-dashoffset", offset);
      const colour = kindColor(view.cookKind);
      if (this.ringFill.getAttribute("stroke") !== colour) this.ringFill.setAttribute("stroke", colour);
      if (p >= 1 && this.lastCook < 1) {
        this.cross.classList.add("pulse");
        window.clearTimeout(this.pulseTimer);
        this.pulseTimer = window.setTimeout(() => this.cross.classList.remove("pulse"), 320);
      }
      this.lastCook = p;
    } else {
      this.lastCook = -1;
    }
  }

  private updateTop(view: HudView): void {
    const overtime = view.phase === 3;
    this.text(this.modeEl, overtime ? "OVERTIME" : "FREE FOR ALL");
    this.cls(this.modeEl, "hot", overtime);
    this.text(this.timeEl, mmss(overtime ? Math.ceil(view.phaseLeftS) : view.matchS));
    this.cls(this.timeEl, "hot", overtime);

    this.text(this.youEl, `YOU ${view.myScore}`);
    // If I lead, show the runner up as 2nd so the row is always a comparison.
    let label = "1ST", name = view.leader, score = view.leaderScore;
    if (view.leader === view.myName) {
      const sorted = sortPlayers(view.players);
      const next = sorted.find((p) => !p.me);
      if (next) { label = "2ND"; name = next.name; score = next.score; }
    }
    const sig = `${label}|${name}|${score}`;
    if (this.changed("lead", sig)) {
      this.leadEl.replaceChildren();
      const b = document.createElement("b");
      b.textContent = label + " ";
      this.leadEl.appendChild(b);
      this.leadEl.appendChild(document.createTextNode(`${name} ${score}`));
    }

    const s = Math.max(0, Math.min(100, view.shield));
    this.style(this.shieldBar, "width", `${s.toFixed(0)}%`);
    this.style(this.shieldBar, "background", s >= 100 ? GREEN : s < 40 ? ORANGE : PAPER);
    this.text(this.shieldNum, String(Math.ceil(s)));
    this.cls(this.shieldEl, "flash", view.flashing);
  }

  private updateSlots(view: HudView): void {
    for (let i = 0; i < 4; i++) {
      const s = view.slots[i] ?? { kind: 0, ammo: 0, label: "" };
      const ui = this.slotEls[i];
      const isKnife = i === 3;
      const empty = !isKnife && s.kind === 0;
      const colour = isKnife ? "#9AA0B4" : empty ? MUTED : kindColor(s.kind);
      this.style(ui.root, "--kc", colour);
      this.cls(ui.root, "sel", view.slot === i + 1);
      this.cls(ui.root, "dim", empty);
      this.text(ui.name, isKnife ? "KNIFE" : empty ? "EMPTY" : (s.label || kindName(s.kind)));
      let ammo: string;
      if (isKnife) ammo = view.knifeOut ? `OUT ${Math.max(0, Math.ceil(view.knifeReturnS))} S` : "READY";
      else if (empty) ammo = "-";
      else ammo = s.ammo < 0 ? "∞" : String(s.ammo);
      this.text(ui.ammo, ammo);
    }
    const cd = Math.max(0, Math.min(1, view.fragCooldown));
    this.style(this.cdBar, "width", `${(cd * 100).toFixed(0)}%`);
    this.style(this.cdBar.parentElement as HTMLElement, "opacity", cd > 0 ? "1" : "0");
  }

  private updatePads(view: HudView): void {
    const sig = view.pads.map((p) => `${p.id}|${p.label}|${p.kind}|${p.active ? 1 : 0}|${Math.ceil(p.nextInS)}`).join(";");
    if (!this.changed("pads", sig)) return;
    this.padsEl.replaceChildren();
    for (const p of view.pads) {
      const dark = p.kind === 0;
      const row = el("div", "row" + (dark ? " dim" : ""), this.padsEl);
      const dot = el("div", "dot", row);
      dot.style.background = dark ? MUTED : kindColor(p.kind);
      el("span", "lbl", row, p.label || kindName(p.kind));
      const st = el("span", "st", row);
      if (dark) st.textContent = "-";
      else if (p.active) { st.textContent = "READY"; st.classList.add("ready"); }
      else st.textContent = `${Math.max(0, Math.ceil(p.nextInS))} S`;
    }
  }

  private updateTable(host: HTMLElement, key: string, view: HudView, showVotes: boolean): void {
    const sorted = sortPlayers(view.players);
    const sig = sorted.map((p) => `${p.id}|${p.name}|${p.score}|${p.kills}|${p.deaths}|${p.me ? 1 : 0}|${p.connected ? 1 : 0}|${p.bot ? 1 : 0}|${showVotes && p.vote ? 1 : 0}`).join(";");
    if (!this.changed(key, sig)) return;
    const table = document.createElement("table");
    table.className = "fo-table";
    const head = table.createTHead().insertRow();
    for (const h of ["", "NAME", "SCORE", "KILLS", "DEATHS"]) {
      const th = document.createElement("th");
      th.textContent = h;
      head.appendChild(th);
    }
    head.firstElementChild!.setAttribute("style", "text-align:right;width:28px");
    const body = table.createTBody();
    sorted.forEach((p, i) => {
      const tr = body.insertRow();
      if (p.me) tr.classList.add("me");
      if (!p.connected) tr.classList.add("away");
      tr.insertCell().textContent = ordinal(i + 1);
      const name = tr.insertCell();
      name.textContent = p.name;
      if (p.bot) el("span", "tag", name, "BOT");
      if (!p.connected) el("span", "tag", name, "AWAY");
      if (showVotes && p.vote) el("span", "tick", name, "✓");
      tr.insertCell().textContent = String(p.score);
      tr.insertCell().textContent = String(p.kills);
      tr.insertCell().textContent = String(p.deaths);
    });
    host.replaceChildren(table);
  }

  private updateLobby(view: HudView): void {
    const counting = view.phase === 1;
    const need = Math.max(0, 2 - (view.humans + view.bots));
    this.text(this.lobbySub, counting ? "STARTING IN" : need > 0 ? `WAITING FOR ${need} MORE` : "WAITING FOR READY");
    this.text(this.lobbyCount, counting ? String(Math.max(0, Math.ceil(view.countdownS))) : `${view.humans + view.bots} OF 8`);

    const sig = view.players.map((p) => `${p.id}|${p.name}|${p.ready ? 1 : 0}|${p.bot ? 1 : 0}|${p.connected ? 1 : 0}|${p.me ? 1 : 0}`).join(";");
    if (this.changed("lobby", sig)) {
      const table = document.createElement("table");
      table.className = "fo-table";
      const body = table.createTBody();
      for (const p of view.players) {
        const tr = body.insertRow();
        if (p.me) tr.classList.add("me");
        if (!p.connected) tr.classList.add("away");
        const name = tr.insertCell();
        name.textContent = p.name;
        if (p.bot) el("span", "tag", name, "BOT");
        if (!p.connected) el("span", "tag", name, "AWAY");
        const st = tr.insertCell();
        if (p.bot || p.ready) { st.textContent = "✓"; st.className = "tick"; st.style.color = GREEN; st.style.textShadow = `1px 1px 0 ${INK}`; }
        else st.textContent = "...";
      }
      this.lobbyList.replaceChildren(table);
    }

    const me = view.players.find((p) => p.me);
    const ready = !!me?.ready;
    this.text(this.readyBtn, ready ? "READY ✓" : "READY");
    this.cls(this.readyBtn, "on", ready);
    this.text(this.lobbyHint, view.hint);
  }

  private updateResults(view: HudView): void {
    this.text(this.winnerEl, view.winner || "NOBODY");
    this.updateTable(this.resultsTable, "results", view, true);
    const humans = view.players.filter((p) => !p.bot);
    const votes = humans.filter((p) => p.vote).length;
    const me = humans.find((p) => p.me);
    this.text(this.voteBtn, `REMATCH ${votes} OF ${humans.length}`);
    this.cls(this.voteBtn, "on", !!me?.vote);
    this.text(this.resultsLeft, `NEXT IN ${Math.max(0, Math.ceil(view.phaseLeftS))} S`);
  }
}
